import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { YOUTH_CLASSES as Y } from "../data/youthClasses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = Y.registration;

const WAIVER =
  "I am the parent or legal guardian of the student named above. I understand this is an art class using paints, scissors, and other studio materials, and I release Joseph Graves Jr. Art and D'Art Gallery from liability for ordinary injuries arising from normal class activity. I will keep emergency contact information current and agree to the sign-in/sign-out, pickup, missed-class, and refund policies published on the Youth Art Classes page.";

const input =
  "w-full bg-transparent border-b border-border py-2.5 text-base outline-none focus:border-foreground transition-colors";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="label mb-2 block">{label}</span>
      {children}
      {hint && <span className="block mt-1.5 text-xs text-[hsl(var(--ink-soft))]">{hint}</span>}
    </label>
  );
}

const INITIAL = {
  parent_name: "",
  email: "",
  phone: "",
  child_name: "",
  child_age: "",
  allergies: "",
  emergency_name: "",
  emergency_phone: "",
  pickup_adults: "",
  photo_permission: "",
  waiver: false,
};

export default function YouthRegister() {
  const [f, setF] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const valid =
    f.parent_name.trim() &&
    /.+@.+\..+/.test(f.email) &&
    f.phone.trim() &&
    f.child_name.trim() &&
    f.child_age &&
    f.emergency_name.trim() &&
    f.emergency_phone.trim() &&
    f.pickup_adults.trim() &&
    f.photo_permission &&
    f.waiver;

  if (!R.open && new URLSearchParams(window.location.search).get("preview") !== "1") {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="youth-register-closed">
        <p className="label mb-4">Youth Art Classes</p>
        <h1 className="serif text-4xl md:text-6xl tracking-tight font-light leading-[1.05] max-w-3xl">
          Registration opens <span className="italic text-[hsl(var(--ink-soft))]">once the dates are set.</span>
        </h1>
        <p className="mt-6 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed">
          Families on the interest list get the dates and the registration link first.
        </p>
        <Link to="/youth-art-classes#interest" className="mt-8 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90" data-testid="youth-register-closed-cta">
          Join the interest list <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/youth-registration`, {
        ...f,
        allergies: f.allergies || null,
        session: R.sessionLabel,
        session_dates: R.dates,
        photo_permission: f.photo_permission === "yes",
        waiver_text: WAIVER,
      });
      setDone(data);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Something went wrong. Please email hello@josephgravesjr.com.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="youth-register-success">
        <div className="inline-flex items-center gap-2 mb-4 text-[hsl(var(--accent))]">
          <Check className="h-4 w-4" />
          <span className="label !text-[hsl(var(--accent))]">Registration received</span>
        </div>
        <h1 className="serif text-4xl md:text-6xl tracking-tight font-light leading-[1.05] max-w-3xl">
          {f.child_name.split(" ")[0]}'s spot is held for 24 hours.
        </h1>
        <p className="mt-6 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed">
          Complete the ${R.price} tuition payment below to confirm the seat. A confirmation with the class dates is on
          its way to {f.email}.
        </p>
        {R.paymentUrl ? (
          <a href={R.paymentUrl} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90" data-testid="youth-register-pay">
            Pay ${R.price} tuition via Square <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        ) : (
          <p className="mt-8 text-sm text-[hsl(var(--ink-soft))]">Joseph will email you a secure payment link shortly.</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="youth-register-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10 md:pb-14">
        <p className="label mb-4">Youth Art Classes · {R.sessionLabel}</p>
        <h1 className="serif text-4xl md:text-6xl tracking-tight font-light leading-[1.05]">Register your child.</h1>
        <p className="mt-5 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed">
          {R.dates.join(" · ")} · Saturdays 10 AM – 12 PM · D'Art Gallery, 900 Santa Fe Drive · ${R.price} for the
          four-week session, all supplies included. Payment is collected after this form.
        </p>
      </header>

      <form onSubmit={submit} noValidate className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32 space-y-14" data-testid="youth-register-form">
        <fieldset className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <legend className="md:col-span-4 serif text-2xl md:text-3xl">Parent or guardian</legend>
          <div className="md:col-span-8 md:col-start-5 grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label="Full name"><input required value={f.parent_name} onChange={set("parent_name")} className={input} data-testid="reg-parent-name" /></Field>
            <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={input} data-testid="reg-email" /></Field>
            <Field label="Mobile phone"><input required type="tel" value={f.phone} onChange={set("phone")} className={input} data-testid="reg-phone" /></Field>
          </div>
        </fieldset>

        <fieldset className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-t border-border pt-12">
          <legend className="md:col-span-4 serif text-2xl md:text-3xl">Student</legend>
          <div className="md:col-span-8 md:col-start-5 grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label="Child's full name"><input required value={f.child_name} onChange={set("child_name")} className={input} data-testid="reg-child-name" /></Field>
            <Field label="Age">
              <select required value={f.child_age} onChange={set("child_age")} className={input} data-testid="reg-child-age">
                <option value="">Select…</option>
                {Y.form.ages.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Allergies, medical needs, or anything Joseph should know" hint="Optional. Includes sensory needs or anything that helps your child have a good class.">
                <textarea rows={3} value={f.allergies} onChange={set("allergies")} className={`${input} resize-none`} data-testid="reg-allergies" />
              </Field>
            </div>
          </div>
        </fieldset>

        <fieldset className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-t border-border pt-12">
          <legend className="md:col-span-4 serif text-2xl md:text-3xl">Emergency & pickup</legend>
          <div className="md:col-span-8 md:col-start-5 grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label="Emergency contact name" hint="Someone other than you."><input required value={f.emergency_name} onChange={set("emergency_name")} className={input} data-testid="reg-emergency-name" /></Field>
            <Field label="Emergency contact phone"><input required type="tel" value={f.emergency_phone} onChange={set("emergency_phone")} className={input} data-testid="reg-emergency-phone" /></Field>
            <div className="md:col-span-2">
              <Field label="Adults authorized to pick up" hint="Names, separated by commas. Students are released only to these adults.">
                <input required value={f.pickup_adults} onChange={set("pickup_adults")} className={input} data-testid="reg-pickup" />
              </Field>
            </div>
          </div>
        </fieldset>

        <fieldset className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-t border-border pt-12">
          <legend className="md:col-span-4 serif text-2xl md:text-3xl">Permissions</legend>
          <div className="md:col-span-8 md:col-start-5 space-y-8">
            <div role="radiogroup" aria-label="Photo permission" data-testid="reg-photo-permission">
              <p className="label mb-3">Photo permission</p>
              {[
                ["yes", "Yes — my child may appear in class photos used by the studio (website, social, press)."],
                ["no", "No — please do not photograph my child."],
              ].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={f.photo_permission === v}
                  onClick={() => setF((s) => ({ ...s, photo_permission: v }))}
                  className="w-full flex items-start gap-4 py-3.5 border-b border-border text-left"
                  data-testid={`reg-photo-${v}`}
                >
                  <span className={`mt-1 h-5 w-5 shrink-0 rounded-full border flex items-center justify-center ${f.photo_permission === v ? "bg-foreground border-foreground text-background" : "border-[hsl(var(--ink-soft))]/50"}`}>
                    {f.photo_permission === v && <Check className="h-3 w-3" />}
                  </span>
                  <span className="text-base leading-relaxed">{label}</span>
                </button>
              ))}
            </div>
            <div>
              <p className="label mb-3">Liability release & policies</p>
              <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed border border-border p-5">{WAIVER}</p>
              <label className="mt-4 flex items-start gap-4 cursor-pointer">
                <input type="checkbox" checked={f.waiver} onChange={set("waiver")} className="mt-1 h-5 w-5 accent-[hsl(var(--accent))]" data-testid="reg-waiver" />
                <span className="text-base">I have read and agree to the release and policies above.</span>
              </label>
            </div>
          </div>
        </fieldset>

        <div className="border-t border-border pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="text-sm text-[hsl(var(--ink-soft))] max-w-md">
            Next: a ${R.price} tuition payment via Square confirms the seat. Full refund up to 7 days before the first class.
          </p>
          <button
            type="submit"
            disabled={!valid || busy}
            className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-40 transition-opacity"
            data-testid="reg-submit"
          >
            {busy ? "Submitting…" : "Submit registration"}
            {!busy && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </form>
    </div>
  );
}
